import { Page, PageBody, PageHeader } from "@/components/app-layout";
import { BaseDialog } from "@/components/dialogs/BaseDialog";
import { Folders } from "@/components/folder/Folders";
import { Button } from "@/components/ui/button";
import { useGrantsData } from "@/data/use-grants-data";
import { useGroupFoldersData } from "@/data/use-group-folder-data";
import { useGroupRepositoryKitData } from "@/data/use-group-repository-kit-data";
import { useKitFoldersData } from "@/data/use-kit-folders-data";
import { useGroupUuid } from "@/hooks/use-group-uuid";
import { queryKeys } from "@/lib/query-keys";
import { Folder } from "@/types/types";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faUnlink } from "@nnr/fontawesome-sharp-light/icons/sharp/light";
import { useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { toast } from "sonner";

export const SharedFoldersPage = () => {
  const groupUuid = useGroupUuid();
  const { folders: groupFolders } = useGroupFoldersData({
    groupUuid,
  });
  const { kit } = useGroupRepositoryKitData(groupUuid);
  const { folders: repositoryFolders } = useKitFoldersData(
    groupUuid,
    kit?.uuid || null,
  );
  const externalFolders = groupFolders.filter(
    (folder) => !repositoryFolders.find((rf) => rf.uuid === folder.uuid),
  );
  const [folderToUnlink, setFolderToUnlink] = useState<Folder | null>(null);

  const handleUnlinkFolder = (folder: Folder) => {
    setFolderToUnlink(folder);
  };

  if (!kit) {
    return null;
  }

  return (
    <>
      <Page>
        <PageHeader>
          <PageHeader.Title>Shared Knowledge</PageHeader.Title>
        </PageHeader>

        <PageBody>
          <div>Folders shared with my workspace.</div>
          <Folders
            folders={externalFolders}
            actions={(folder) => (
              <Button
                onClick={() => handleUnlinkFolder(folder)}
                size="icon"
                variant="ghost"
                aria-label="Unlink"
                title="Unlink"
              >
                <FontAwesomeIcon icon={faUnlink} />
              </Button>
            )}
          />
        </PageBody>
      </Page>

      {folderToUnlink && groupUuid && (
        <UnlinkDialog
          folder={folderToUnlink}
          groupUuid={groupUuid}
          open={!!folderToUnlink}
          onOpenChange={() => setFolderToUnlink(null)}
          onConfirmClick={() => setFolderToUnlink(null)}
        />
      )}
    </>
  );
};

const UnlinkDialog = ({
  folder,
  groupUuid,
  open,
  onOpenChange,
  onConfirmClick,
}: {
  folder: Folder;
  groupUuid: string;
  open: boolean;
  onOpenChange: () => void;
  onConfirmClick: () => void;
}) => {
  const { revoke } = useGrantsData({
    folderUuid: folder.uuid,
  });
  const queryClient = useQueryClient();

  const handleConfirmClick = async () => {
    await revoke({
      groupUuid: groupUuid,
    });
    onConfirmClick();
    queryClient.invalidateQueries({
      queryKey: queryKeys.groupFolders({
        groupUuid,
      }),
    });
    toast.success(`Unlinked ${folder.name} successfully.`);
  };

  return (
    <BaseDialog
      title={`Unlink ${folder.name || "External Knowledge"}`}
      description="Are you sure you want to unlink this external knowledge from your workspace? The folder and its contents will remain in the original group."
      confirmLabel="Unlink"
      onConfirmClick={handleConfirmClick}
      onOpenChange={onOpenChange}
      open={open}
    />
  );
};
